import { Controller } from '@hotwired/stimulus';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import {
  installPlyrPrototypeCaptionsFix,
  nudgeYouTubeCaptionsIfNeeded,
  shimCaptionsLanguagesIfNeeded,
  wireYouTubeEmbedCaptionToggle,
  withProviderEmbedCaptions,
} from '../plyr_embed_caption_options';

installPlyrPrototypeCaptionsFix();

/**
 * Plyr Controller
 * 
 * Initializes Plyr video/audio player on media elements within the controller scope.
 * Stores player instance on the element for access by the URL refresh system.
 * 
 * Usage:
 *   <div data-controller="plyr">
 *     <video controls>
 *       <source src="..." type="video/mp4">
 *     </video>
 *   </div>
 * 
 * Or directly on the media element:
 *   <video data-controller="plyr" controls>
 *     <source src="..." type="video/mp4">
 *   </video>
 */
export default class PlyrController extends Controller {
  static values = {
    autoplay: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    hideControls: { type: Boolean, default: false }
  };

  connect() {
    this.players = [];
    this.initializePlayers();
  }

  disconnect() {
    this.destroyPlayers();
  }

  initializePlayers() {
    // Find all video and audio elements within the controller scope
    const mediaElements = this.findMediaElements();
    
    mediaElements.forEach((element) => {
      // Skip if already initialized
      if (element._plyrInstance) return;
      
      const player = this.createPlayer(element);
      if (player) {
        this.players.push(player);
        // Store reference on the DOM element for access by refresh system
        element._plyrInstance = player;
      }
    });
  }

  findMediaElements() {
    // If the controller is directly on a media element, return just that element
    if (this.element.matches('video, audio, .plyr__video-embed, [data-plyr-provider][data-plyr-embed-id]')) {
      return [this.element];
    }
    
    // Otherwise, find all media elements within the container
    return Array.from(this.element.querySelectorAll('video, audio, .plyr__video-embed, [data-plyr-provider][data-plyr-embed-id]'));
  }

  createPlayer(element) {
    try {
      const options = this.buildOptions(element);
      // If this is a provider embed container, clear any placeholder content first.
      if (element && element.classList && element.classList.contains('plyr__video-embed')) {
        element.innerHTML = '';
      }
      const player = new Plyr(element, options);
      shimCaptionsLanguagesIfNeeded(player);
      nudgeYouTubeCaptionsIfNeeded(player);
      wireYouTubeEmbedCaptionToggle(player);

      player.on('ready', () => {
        console.debug('[Plyr] Player ready:', element);
      });
      
      player.on('error', (event) => {
        console.error('[Plyr] Player error:', event);
      });
      
      return player;
    } catch (error) {
      console.error('[Plyr] Failed to initialize player:', error);
      return null;
    }
  }

  buildOptions(element) {
    const isVideo =
      element.tagName.toLowerCase() === 'video' ||
      element.getAttribute('data-plyr-provider') === 'youtube' ||
      element.getAttribute('data-plyr-provider') === 'vimeo';
    const isAutoplay = this.autoplayValue || element.hasAttribute('autoplay');
    const isMuted = this.mutedValue || element.hasAttribute('muted');
    const isLoop = this.loopValue || element.hasAttribute('loop');
    const hideControls = this.hideControlsValue;
    
    // Base configuration
    const options = {
      // Controls to display
      controls: hideControls ? [] : [
        'play-large',
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'captions',
        'settings',
        'airplay',
        'fullscreen'
      ],
      
      // Settings menu options
      settings: ['captions', 'quality', 'speed', 'loop'],
      
      // Internationalization
      i18n: {
        restart: 'Restart',
        rewind: 'Rewind {seektime}s',
        play: 'Play',
        pause: 'Pause',
        fastForward: 'Forward {seektime}s',
        seek: 'Seek',
        seekLabel: '{currentTime} of {duration}',
        played: 'Played',
        buffered: 'Buffered',
        currentTime: 'Current time',
        duration: 'Duration',
        volume: 'Volume',
        mute: 'Mute',
        unmute: 'Unmute',
        enableCaptions: 'Enable captions',
        disableCaptions: 'Disable captions',
        enterFullscreen: 'Enter fullscreen',
        exitFullscreen: 'Exit fullscreen',
        frameTitle: 'Player for {title}',
        captions: 'Captions',
        settings: 'Settings',
        pip: 'PIP',
        menuBack: 'Go back to previous menu',
        speed: 'Speed',
        normal: 'Normal',
        quality: 'Quality',
        loop: 'Loop'
      },
      
      // Tooltips
      tooltips: {
        controls: true,
        seek: true
      },
      
      // Keyboard shortcuts
      keyboard: {
        focused: true,
        global: false
      },
      
      // Captions
      captions: {
        active: false,
        language: 'auto',
        update: false
      },
      
      // Fullscreen
      fullscreen: {
        enabled: true,
        fallback: true,
        iosNative: false
      },
      
      // Speed options
      speed: {
        selected: 1,
        options: [0.5, 0.75, 1, 1.25, 1.5, 2]
      },
      
      // Quality (for adaptive streaming)
      quality: {
        default: 576,
        options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
      },
      
      // Behavior
      loop: { active: isLoop },
      autoplay: isAutoplay,
      muted: isMuted,
      clickToPlay: true,
      disableContextMenu: false,
      hideControls: true,
      resetOnEnd: false,
      
      // Seek time (seconds)
      seekTime: 10,
      
      // Volume
      volume: 1,
      
      // Storage (remember user preferences)
      storage: {
        enabled: true,
        key: 'plyr'
      }
    };
    
    // Adjust controls for audio (not YouTube/Vimeo embed divs)
    if (!isVideo) {
      options.controls = hideControls ? [] : [
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'settings'
      ];
    }

    return withProviderEmbedCaptions(options, element);
  }

  destroyPlayers() {
    this.players.forEach((player) => {
      try {
        // Get the media element before destroying
        const mediaElement = player.media;
        
        // Destroy the Plyr instance
        player.destroy();
        
        // Clean up the reference
        if (mediaElement) {
          delete mediaElement._plyrInstance;
        }
      } catch (error) {
        console.error('[Plyr] Error destroying player:', error);
      }
    });
    
    this.players = [];
  }

  // Public method to refresh media source gracefully
  // Called by refresh_signed_blob_urls.js when URLs are updated
  refreshSource(newUrl, contentType) {
    this.players.forEach((player) => {
      const mediaElement = player.media;
      if (!mediaElement) return;
      
      // Save current playback state
      const state = {
        currentTime: mediaElement.currentTime,
        paused: mediaElement.paused,
        volume: mediaElement.volume,
        muted: mediaElement.muted,
        playbackRate: mediaElement.playbackRate
      };
      
      // Update the source
      const sourceElement = mediaElement.querySelector('source');
      if (sourceElement) {
        sourceElement.setAttribute('src', newUrl);
        if (contentType) {
          sourceElement.setAttribute('type', contentType);
        }
      }
      
      // Reload the media
      mediaElement.load();
      
      // Restore state when media is ready
      const restoreState = () => {
        mediaElement.currentTime = state.currentTime;
        mediaElement.volume = state.volume;
        mediaElement.muted = state.muted;
        mediaElement.playbackRate = state.playbackRate;
        
        if (!state.paused) {
          mediaElement.play().catch((err) => {
            console.debug('[Plyr] Auto-resume blocked:', err.message);
          });
        }
        
        mediaElement.removeEventListener('loadedmetadata', restoreState);
      };
      
      mediaElement.addEventListener('loadedmetadata', restoreState);
    });
  }
}

